import React, { useState, useEffect } from 'react';
import { FaPaperPlane, FaPlus, FaTrash, FaArrowLeft, FaTimes } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWebSocket } from '../utils/WebSocketContext';
import api from '../services/api';
import getProfileImageUrl from '../utils/getProfileImageUrl';
import '../styles/InBox.css';

function InBox() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [newRecipient, setNewRecipient] = useState('');
  const [showNewMessagePrompt, setShowNewMessagePrompt] = useState(false);
  const [newRecipientError, setNewRecipientError] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const initialRecipientUsername = queryParams.get('recipient_username');

  const ws = useWebSocket();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('Token mevcut değil');
      return;
    }

    const fetchConversations = async () => {
      try {
        const response = await api.get('/messages', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        const updatedConversations = response.data.map((conv, index) => ({
          ...conv,
          id: index,
          profile_image_url: getProfileImageUrl(conv.profile_image_url)
        }));

        updatedConversations.sort((a, b) => {
          const lastMessageA = a.messages?.length > 0 ? a.messages[a.messages.length - 1].created_at : a.created_at;
          const lastMessageB = b.messages?.length > 0 ? b.messages[b.messages.length - 1].created_at : b.created_at;
          return new Date(lastMessageB) - new Date(lastMessageA);
        });

        setConversations(updatedConversations);

        if (initialRecipientUsername) {
          const existingConversation = updatedConversations.find(conv => conv.username === initialRecipientUsername);
          if (existingConversation) {
            setSelectedConversation(existingConversation);
          } else {
            await createNewConversation(initialRecipientUsername);
          }
        }
      } catch (error) {
        console.error('Konuşmalar alınamıyor:', error);
      }
    };

    fetchConversations();
  }, [initialRecipientUsername]);

  useEffect(() => {
    if (selectedConversation?.user_id) {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('Token mevcut değil');
        return;
      }

      const fetchMessages = async () => {
        try {
          const response = await api.get(`/messages/${selectedConversation.user_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          setSelectedConversation(prev => ({
            ...prev,
            messages: response.data
          }));
        } catch (error) {
          console.error('Mesajlar alınamıyor:', error);
        }
      };

      fetchMessages();
    }
  }, [selectedConversation]);

  useEffect(() => {
    if (ws) {
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.type === 'new_message') {
          setConversations(prev => {
            const updatedConversations = prev.map(conv => {
              if (conv.user_id === data.message.sender_id || conv.user_id === data.message.recipient_id) {
                return {
                  ...conv,
                  messages: [...(conv.messages || []), data.message]
                };
              }
              return conv;
            });

            updatedConversations.sort((a, b) => {
              const lastMessageA = a.messages?.length > 0 ? a.messages[a.messages.length - 1].created_at : a.created_at;
              const lastMessageB = b.messages?.length > 0 ? b.messages[b.messages.length - 1].created_at : b.created_at;
              return new Date(lastMessageB) - new Date(lastMessageA);
            });

            return updatedConversations;
          });
        }
      };
    }
  }, [ws]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('Token mevcut değil');
      return;
    }

    try {
      const response = await api.post('/messages', {
        recipient_username: selectedConversation.username,
        content: newMessage
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setConversations(prev => {
        const updatedConversations = prev.map(conv => {
          if (conv.user_id === selectedConversation.user_id) {
            return {
              ...conv,
              messages: [...(conv.messages || []), response.data]
            };
          }
          return conv;
        });

        updatedConversations.sort((a, b) => {
          const lastMessageA = a.messages?.length > 0 ? a.messages[a.messages.length - 1].created_at : a.created_at;
          const lastMessageB = b.messages?.length > 0 ? b.messages[b.messages.length - 1].created_at : b.created_at;
          return new Date(lastMessageB) - new Date(lastMessageA);
        });

        return updatedConversations;
      });

      setNewMessage('');
    } catch (error) {
      console.error('Mesaj gönderim hatası:', error);
    }
  };

  const handleNewConversation = () => {
    setShowNewMessagePrompt(true);
    setNewRecipientError(null);
  };

  const createNewConversation = async (recipientUsername) => {
    const usernameToUse = recipientUsername || newRecipient;

    if (!usernameToUse.trim()) return;

    const token = localStorage.getItem('token');
    const currentUsername = localStorage.getItem('username');

    if (currentUsername && usernameToUse === currentUsername) {
      alert('Kendinize mesaj gönderemezsiniz.');
      setShowNewMessagePrompt(false);
      setNewRecipient('');
      setNewRecipientError('Kendi kullanıcı adınızla bir konuşma başlatılamaz.');
      return;
    }

    if (!token) {
      console.error('Token mevcut değil');
      return;
    }

    const existingConversation = conversations.find(conv => conv.username === usernameToUse);

    if (existingConversation) {
      setSelectedConversation(existingConversation);
      setShowNewMessagePrompt(false);
      setNewRecipient('');
      return;
    }

    try {
      const userResponse = await api.get(`/messages/users/${usernameToUse}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (userResponse.data) {
        const newConv = {
          user_id: userResponse.data.user_id,
          username: userResponse.data.username,
          full_name: userResponse.data.full_name,
          profile_image_url: getProfileImageUrl(userResponse.data.profile_image_url),
          messages: []
        };

        setConversations(prev => {
          const updatedConversations = [...prev, newConv];

          updatedConversations.sort((a, b) => {
            const lastMessageA = a.messages?.length > 0 ? a.messages[a.messages.length - 1].created_at : a.created_at;
            const lastMessageB = b.messages?.length > 0 ? b.messages[b.messages.length - 1].created_at : b.created_at;
            return new Date(lastMessageB) - new Date(lastMessageA);
          });

          return updatedConversations;
        });

        setSelectedConversation(newConv);
        setNewRecipient('');
        setShowNewMessagePrompt(false);
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setNewRecipientError('Lütfen geçerli bir kullanıcı adı girin.');
      } else {
        setNewRecipientError('Bir hata oluştu. Lütfen tekrar deneyin.');
      }
    }
  };

  const handleDeleteConversation = async () => {
    if (!selectedConversation) return;

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('Token mevcut değil');
      return;
    }

    try {
      await api.delete(`/messages/${selectedConversation.user_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setConversations(prevConversations =>
        prevConversations.filter(conv => conv.user_id !== selectedConversation.user_id)
      );

      setSelectedConversation(null);
      setShowDeleteConfirmation(false);
    } catch (error) {
      console.error('Konuşma silinemiyor:', error);
    }
  };

  const handleBackToConversations = () => {
    setSelectedConversation(null);
  };

  return (
    <div className="chat-container-wrapper">
      <div className="chat-container">
        <div className={`conversation-list ${isMobile && selectedConversation ? 'mobile-hidden' : ''}`}>
          <div className="conversation-header">
            <h2>Mesajlar</h2>
            <button className="new-conversation-button" onClick={handleNewConversation}>
              <FaPlus />
              <span>Yeni Mesaj</span>
            </button>
          </div>
          <div className="conversation-items">
            {conversations.map((conv, index) => (
              <div
                key={index}
                className={`conversation-item ${selectedConversation?.user_id === conv.user_id ? 'active' : ''}`}
                onClick={() => setSelectedConversation(conv)}
              >
                <div className="avatar-container">
                  <img src={conv.profile_image_url || 'default.png'} alt={conv.username} className="avatar" />
                </div>
                <div className="conversation-info">
                  <div className="conversation-header-info">
                    <span className="user-name">{conv.username}</span>
                    <span className="message-time">
                      {conv.messages?.length > 0 
                        ? new Date(conv.messages[conv.messages.length - 1].created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                        : ''
                      }
                    </span>
                  </div>
                  <span className="last-message">{conv.content || 'Yeni konuşma'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={`chat-area ${isMobile && !selectedConversation ? 'mobile-hidden' : ''}`}>
          {selectedConversation ? (
            <>
              <div className="chat-header">
                {isMobile && (
                  <button className="back-button" onClick={handleBackToConversations}>
                    <FaArrowLeft />
                  </button>
                )}
                <div className="chat-header-info">
                  <div className="avatar-container">
                    <img
                      src={selectedConversation.profile_image_url || 'default.png'}
                      alt={selectedConversation.username}
                      className="avatar"
                    />
                  </div>
                  <div className="header-text">
                    <span className="user-name">{selectedConversation.username}</span>
                  </div>
                </div>
                <button 
                  className="delete-button" 
                  onClick={() => setShowDeleteConfirmation(true)}
                  title="Konuşmayı sil"
                >
                  <FaTrash />
                </button>
              </div>
              
              <div className="message-list">
                {selectedConversation.messages?.map(message => (
                  <div
                    key={message.message_id}
                    className={`message ${message.sender_id === selectedConversation.user_id ? 'received' : 'sent'}`}
                  >
                    <div className="message-content">{message.content}</div>
                    <div className="message-timestamp">
                      {new Date(message.created_at).toLocaleString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: '2-digit'
                      })}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="message-input">
                <div className="input-container">
                  <input
                    type="text"
                    placeholder="Mesajınızı yazın..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  />
                  <button 
                    className="send-button" 
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                  >
                    <FaPaperPlane />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="no-conversation">
              <div className="no-conversation-content">
                <FaPaperPlane className="no-conversation-icon" />
                <h3>Mesajlaşmaya başlayın</h3>
                <p>Bir konuşma seçin veya yeni bir mesaj gönderin</p>
              </div>
            </div>
          )}
        </div>

        {showNewMessagePrompt && (
          <div className="modal-overlay">
            <div className="modal-content new-message-modal">
              <div className="modal-header">
                <h3>Yeni Mesaj</h3>
                <button className="close-button" onClick={() => setShowNewMessagePrompt(false)}>
                  <FaTimes />
                </button>
              </div>
              <div className="modal-body">
                <div className="input-group">
                  <label htmlFor="recipient-input">Kullanıcı adı</label>
                  <input
                    id="recipient-input"
                    type="text"
                    placeholder="Alıcının kullanıcı adını girin"
                    value={newRecipient}
                    onChange={(e) => setNewRecipient(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && createNewConversation('')}
                  />
                  {newRecipientError && <p className="error-message">{newRecipientError}</p>}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowNewMessagePrompt(false)}>
                  İptal
                </button>
                <button className="btn-primary" onClick={() => createNewConversation('')}>
                  Başlat
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteConfirmation && (
          <div className="modal-overlay">
            <div className="modal-content delete-confirmation-modal">
              <div className="modal-header">
                <h3>Konuşmayı Sil</h3>
              </div>
              <div className="modal-body">
                <p>Bu konuşmayı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</p>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowDeleteConfirmation(false)}>
                  İptal
                </button>
                <button className="btn-danger" onClick={handleDeleteConversation}>
                  Sil
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default InBox;